import { useState, useEffect } from 'react'

const INSTALL_DISMISSED_KEY = 'lm-install-dismissed'

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream
}

export function isInstalledPwa(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) {
    return true
  }
  return (navigator as Navigator & { standalone?: boolean }).standalone === true
}

export function isInstallDismissed(): boolean {
  try {
    return localStorage.getItem(INSTALL_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

export function dismissInstall(): void {
  try {
    localStorage.setItem(INSTALL_DISMISSED_KEY, '1')
  } catch {
    // Private mode: the banner returns next visit.
  }
}

/**
 * Install prompt. Android/Chrome uses the `beforeinstallprompt` event;
 * iOS Safari has none, so iPhones get install guidance instead (U1: push
 * on iOS only works for the installed PWA on 16.4+).
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<Event & { prompt: () => void; userChoice: Promise<unknown> } | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [showIosGuide, setShowIosGuide] = useState(false)

  useEffect(() => {
    if (isInstallDismissed()) return
    if (isIosDevice() && !isInstalledPwa()) {
      setShowIosGuide(true)
      return
    }
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as typeof deferredPrompt)
      setShowPrompt(true)
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstall = () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    void deferredPrompt.userChoice.then(() => {
      setShowPrompt(false)
      setDeferredPrompt(null)
    })
  }

  const handleDismiss = () => {
    dismissInstall()
    setShowPrompt(false)
    setShowIosGuide(false)
  }

  if (showIosGuide) {
    return (
      <div className="fixed bottom-4 left-1/2 z-50 w-[calc(100%-2rem)] max-w-[448px] -translate-x-1/2 rounded-lg border border-lm-primary bg-lm-surface p-4 shadow-lg">
        <p className="font-medium text-lm-on-surface">Install LM Mobile</p>
        <p className="mt-1 text-sm text-lm-on-surface-variant">
          On iPhone, tap Share, then “Add to Home Screen”. Push notifications need the installed app.
        </p>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button onClick={handleDismiss} className="px-3 py-2 text-sm text-lm-on-surface-variant hover:text-lm-on-surface">
            Got it
          </button>
        </div>
      </div>
    )
  }

  if (!showPrompt) return null

  return (
    <div className="fixed bottom-4 left-1/2 w-[calc(100%-2rem)] max-w-[448px] -translate-x-1/2 bg-lm-surface border border-lm-primary rounded-lg p-4 flex items-center justify-between z-50 shadow-lg">
      <div>
        <p className="text-lm-on-surface font-medium">Install LM Mobile</p>
        <p className="text-lm-on-surface-variant text-sm">Add to home screen for quick access</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={handleDismiss}
          className="text-lm-on-surface-variant hover:text-lm-on-surface px-3 py-2 text-sm"
        >
          Later
        </button>
        <button
          onClick={handleInstall}
          className="bg-lm-primary text-lm-on-primary px-4 py-2 rounded-lg font-medium hover:bg-lm-primary/90 transition-colors"
        >
          Install
        </button>
      </div>
    </div>
  )
}
